import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { RobuxIcon, WalletValue, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Swords, Users, Plus, Minus, X, Activity, Handshake, Gem, Dices, Flame, ArrowDown, ArrowUp, Search, Zap, Target, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';
import { DynamicCasePreview } from '../components/CasePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Kısaltmalı format (1.37K, milyon+ → 1,000,000) — CaseBattles/CommunityCases ile aynı.
// Intl "compact" desteğine GÜVENMEZ; her ortamda çalışsın diye elle hesaplanır.
const formatCompact = (number) => {
  const raw = typeof number === 'string' ? parseFloat(number.replace(/,/g, '')) : Number(number);
  if (!raw || isNaN(raw)) return "0";
  const sign = raw < 0 ? "-" : "";
  const abs = Math.abs(raw);
  const trim = (v) => v.toFixed(2).replace(/\.?0+$/, "");
  if (abs >= 1000000) return sign + abs.toLocaleString();
  if (abs >= 1e3) return sign + trim(abs / 1e3) + "K";
  return sign + trim(abs);
};

// ==========================================
// DYNAMIC CASE PREVIEW COMPONENT (DÜZ VE ORANLANMIŞ)
// ==========================================
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı: ALTI dosyada altı kopya zamanla
// ayrışıyor ve her düzeltme altı yere dokunmayı gerektiriyordu.
export const CreateBattle = () => {
  const { token, isAuthenticated, fmtMoney, balance, wallet } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔁 COPY BATTLE — BattleView'deki "Copy Battle" butonundan gelen yapılandırma.
  // Veri ÖNCE router navigation state'ten, yoksa sessionStorage'dan okunur (sayfa
  // yenilense bile korunsun diye iki kaynak). TEK SEFERLİKtir: okununca temizlenir ki
  // daha sonra normal "Create Battle" ziyaretlerinde eski seçim geri gelmesin.
  const copyInit = useMemo(() => {
    let payload = location.state?.copyBattle || null;
    if (!payload) {
      try {
        const raw = sessionStorage.getItem('copyBattleConfig');
        if (raw) payload = JSON.parse(raw);
      } catch (_) { /* sessionStorage erişilemezse sessizce yok say */ }
    }
    try { sessionStorage.removeItem('copyBattleConfig'); } catch (_) {}
    return payload && typeof payload === 'object' ? payload : null;
    // Yalnızca ilk mount'ta çalışsın (tek seferlik tüketim).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [officialCases, setOfficialCases] = useState([]);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  // 🔁 Kopyalanan kasalar varsa AYNI SIRAYLA önceden seçili gelir (kasa sırası korunur).
  // Her örneğe yeni tempId verilir (sağ liste gruplama/azaltma id ile çalışır).
  const [selectedCases, setSelectedCases] = useState(() =>
    Array.isArray(copyInit?.cases)
      ? copyInit.cases.map(c => ({ ...c, tempId: Math.random() }))
      : []
  );
  // 🔍 View Drops: hangi kasanın içeriği gösteriliyor (null = kapalı)
  const [dropsModalCase, setDropsModalCase] = useState(null);

  // 🌟 Kasa kaynağı: 'donutluck' (resmi) | 'community' — varsayılan BloxBets
  const [caseSource, setCaseSource] = useState('donutluck');
  // 🌟 Sıralama: 'popularity' | 'price' + yön 'high' (çoktan aza) | 'low' (azdan çoğa)
  const [sortBy, setSortBy] = useState('popularity');
  const [sortDir, setSortDir] = useState('high');

  // 🔍 Arama (debounced)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // 🌟 Community: SUNUCU-TARAFLI sayfalama (lag çözümü — hepsini bir anda yüklemeyiz)
  const COMMUNITY_PAGE = 30;
  const [communityCases, setCommunityCases] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityHasMore, setCommunityHasMore] = useState(false);
  const [communityLoadingMore, setCommunityLoadingMore] = useState(false);

  const [mode, setMode] = useState(copyInit?.mode || '1v1');
  // 🌟 BASE oyun modu ('classic', 'shared', 'biggest_pull', 'jackpot')
  // Crazy ve Terminal artık ayrı MODIFIER'dır (classic & jackpot ile birleşebilir).
  const [uiGameMode, setUiGameMode] = useState(copyInit?.game_mode || 'classic');
  // 🌟 MODIFIER'lar — backend is_crazy_mode / is_terminal_mode'a birebir maplenir.
  const [isCrazy, setIsCrazy] = useState(!!copyInit?.is_crazy_mode);
  const [isTerminal, setIsTerminal] = useState(!!copyInit?.is_terminal_mode);
  // Modifier'lar YALNIZCA classic ve jackpot ile geçerli (backend validasyonu ile aynı).
  const modifiersAllowed = uiGameMode === 'classic' || uiGameMode === 'jackpot';
  // ⚡ YENİ: Hızlı mod — kasalar daha hızlı döner/açılır (sadece zamanlama, ödüller değişmez)
  const [fastMode, setFastMode] = useState(!!copyInit?.fast_mode);

  // 🐷 YENİ: BORROW (Borç/Kaldıraç) — battle seviyesinde.
  // borrowEnabled kapalıyken borrow_percent=0 gönderilir (borrow yok).
  // borrowPercent: 1..80 arası (sağda max %80, solda min %1). Giriş ücreti ve kazanç
  // bu oranda küçülür (backend ile birebir uyumlu; house edge korunur).
  const [borrowEnabled, setBorrowEnabled] = useState((Number(copyInit?.borrow_percent) || 0) > 0);
  const [borrowPercent, setBorrowPercent] = useState(() => {
    const p = Number(copyInit?.borrow_percent) || 0;
    return p > 0 ? Math.max(1, Math.min(80, Math.round(p))) : 40;
  });
  
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  /* ══════════════════════════════════════════════════════════════════
     🍀 CÜZDAN DEĞİŞİMİ — TEK BİR SİNYALE İNDİRGENİR
     ══════════════════════════════════════════════════════════════════
     Backend kasa fiyatlarını AKTİF CÜZDANIN biriminde gönderiyor. Liste
     tazelenmezse eski birimdeki fiyatlar ekranda kalır ve YENİ cüzdanın
     biçimlendiricisiyle basılır (14.000 site birimlik kasa BB'ye geçince
     "14 BB" görünüyordu; doğrusu 2.380 BB).

     🔴 KAPATILAN HATA: eski kod bu tazelemeyi YALNIZCA resmi kasalar için
     yapıyordu. Community listesinin effect'inde `wallet` bağımlılığı YOKTU
     ve `wallet:changed` dinleyicisi de ona dokunmuyordu. Sonuç: Community
     sekmesi açıkken cüzdan değiştirilince kasalar ESKİ cüzdanın fiyatlarını
     taşımaya devam ediyor, ekranda yeni cüzdanın birimiyle basılıyordu —
     sayfa yenilenince düzelmesinin sebebi buydu.
     Ayrıca `decorate_cases_for_wallet` bazı kasaları cüzdana göre listeden
     DÜŞÜRÜYOR; tazelenmeyen listede o kasalar da görünmeye devam ediyordu.

     ⚠️ İKİ SİNYAL BİRDEN DİNLENİR. Cüzdan değişimi hem `useAuth().wallet`
     state'i hem `wallet:changed` penceresi olayı ile gelebiliyor; hangisinin
     önce geldiğine güvenmek kırılgan. İkisi de aynı sayaca akıyor. */
  const [walletEpoch, setWalletEpoch] = useState(0);
  useEffect(() => {
    const bump = () => setWalletEpoch(e => e + 1);
    window.addEventListener('wallet:changed', bump);
    return () => window.removeEventListener('wallet:changed', bump);
  }, []);

  /* Cüzdanın "sürümü": her değişimde farklılaşır. Hem effect bağımlılığı
     hem de uçuştaki isteklerin geçerliliğini ölçmek için kullanılır. */
  const walletKey = `${wallet ?? ''}|${walletEpoch}`;
  const walletKeyRef = useRef(walletKey);
  useEffect(() => { walletKeyRef.current = walletKey; }, [walletKey]);

  /* Cüzdana BAĞLI türetilmiş durumun tamamı temizlenir.
     ⚠️ İLK ÇALIŞMA ATLANIR: `selectedCases` "battle kopyala" özelliğinden
     (`copyInit.cases`) doluyor. Mount'ta temizlemek kopyalanan savaşın
     kasalarını silerdi. */
  const walletFirstRun = useRef(true);
  useEffect(() => {
    if (walletFirstRun.current) { walletFirstRun.current = false; return; }
    setSelectedCases([]);
    // ⚠️ Drop modalı kasa nesnesinin KOPYASINI tutuyor; item fiyatları eski
    // birimde kalırdı. Kapatılır.
    setDropsModalCase(null);
    // Community listesi de eski birimde — tek kare bile gösterilmesin.
    setCommunityCases([]);
    setCommunityHasMore(false);
  }, [walletKey]);

  // Resmi (BloxBets) kasalar az sayıda → hepsini bir kez çek, client-side filtrele/sırala
  useEffect(() => {
    /* ⚠️ YARIŞ KORUMASI: cüzdan hızlı iki kez değişirse ESKİ isteğin cevabı
       sonra gelip yeni listeyi ezebilir. Her cevap, kendi isteğinin
       başlatıldığı cüzdan sürümüyle doğrulanır. */
    const myKey = walletKey;
    let cancelled = false;
    const fetchOfficial = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/game/cases`);
        if (cancelled || walletKeyRef.current !== myKey) return;
        setOfficialCases(res.data || []);
      } catch (error) {
        if (!cancelled) toast.error("Could not load cases.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOfficial();
    return () => { cancelled = true; };
  }, [walletKey]);

  // 🔍 Arama debounce (300ms) — her tuşta sunucuya gitmesin
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Frontend sort + dir → backend sort param
  const serverSort = `${sortBy === 'popularity' ? 'popularity' : 'price'}_${sortDir === 'high' ? 'desc' : 'asc'}`;

  // 🌟 Community: arama/sıralama değişince ilk sayfayı (skip=0) çek
  useEffect(() => {
    if (caseSource !== 'community') return;
    let cancelled = false;
    const fetchFirst = async () => {
      setCommunityLoading(true);
      try {
        const params = new URLSearchParams({ limit: COMMUNITY_PAGE, skip: 0, sort: serverSort });
        if (debouncedSearch) params.set('search', debouncedSearch);
        const res = await axios.get(`${BACKEND_URL}/api/game/cases/community?${params.toString()}`);
        if (cancelled) return;
        const data = res.data || [];
        setCommunityCases(data);
        setCommunityHasMore(data.length === COMMUNITY_PAGE);
      } catch (error) {
        if (!cancelled) toast.error("Could not load community cases.");
      } finally {
        if (!cancelled) setCommunityLoading(false);
      }
    };
    fetchFirst();
    return () => { cancelled = true; };
    /* ⚠️ `walletKey` BAĞIMLILIĞI ŞART — asıl hata buradaydı.
       Bu liste sunucudan AKTİF CÜZDANIN biriminde geliyor ve cüzdana göre
       bazı kasalar hiç dönmüyor (`decorate_cases_for_wallet` `usd_ok`
       olmayanları düşürüyor). Bağımlılık olmadan cüzdan değişse bile
       effect yeniden çalışmıyor, ekranda eski cüzdanın fiyatları yeni
       cüzdanın birimiyle basılıyordu. */
  }, [caseSource, debouncedSearch, serverSort, walletKey]);

  // "Load More" → bir sonraki sayfayı çekip ekle
  const loadMoreCommunity = async () => {
    if (communityLoadingMore || !communityHasMore) return;
    /* ⚠️ İSTEK BAŞLARKENKİ CÜZDAN SÜRÜMÜ SAKLANIR.
       "Load More" uçarken kullanıcı cüzdan değiştirirse, gelen sayfa ESKİ
       cüzdanın fiyatlarını taşır. Guard olmadan bu sayfa yeni listenin
       SONUNA eklenir ve tek listede iki farklı birim karışırdı — üstelik
       `skip` de kaymış olur, aynı kasalar tekrar gelirdi. */
    const myKey = walletKeyRef.current;
    setCommunityLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: COMMUNITY_PAGE, skip: communityCases.length, sort: serverSort });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await axios.get(`${BACKEND_URL}/api/game/cases/community?${params.toString()}`);
      if (walletKeyRef.current !== myKey) return;   // cüzdan değişti → sayfa ATILIR
      const data = res.data || [];
      setCommunityCases(prev => [...prev, ...data]);
      setCommunityHasMore(data.length === COMMUNITY_PAGE);
    } catch (error) {
      if (walletKeyRef.current === myKey) toast.error("Could not load more cases.");
    } finally {
      // Cüzdan değişmiş olsa bile spinner kapatılır: aksi halde buton
      // sonsuza dek "yükleniyor" kalırdı.
      setCommunityLoadingMore(false);
    }
  };

  // 🌟 Popülerlik = toplam açılma × (fiyat / 10) — BloxBets client-side sıralaması için
  const popularityOf = (c) => (c.total_opens || 0) * ((c.price || 0) / 10);

  // 🌟 Gösterilecek liste:
  //   BloxBets → client-side filtre (arama) + sıralama (az sayıda, lag yok)
  //   Community → sunucudan zaten filtrelenmiş + sıralanmış + sayfalanmış geldi
  const displayedCases = useMemo(() => {
    if (caseSource === 'community') return communityCases;
    let arr = [...officialCases];
    const q = debouncedSearch.toLowerCase();
    if (q) arr = arr.filter(c => (c.name || '').toLowerCase().includes(q));
    arr.sort((a, b) => {
      const va = sortBy === 'popularity' ? popularityOf(a) : (a.price || 0);
      const vb = sortBy === 'popularity' ? popularityOf(b) : (b.price || 0);
      let cmp = vb - va;                 // 'high' = çoktan aza (varsayılan)
      if (cmp === 0) cmp = (b.price || 0) - (a.price || 0); // eşitlikte fiyata göre stabil
      return sortDir === 'low' ? -cmp : cmp;
    });
    return arr;
  }, [caseSource, officialCases, communityCases, debouncedSearch, sortBy, sortDir]);

  const poolLoading = caseSource === 'community' ? communityLoading : loading;

  const isTeamMode = mode === '2v2' || mode === '2v2v2' || mode === '3v3';

  // 🛡️ MANTIK ZIRHI: Eğer "Team Battle" seçiliyse "Shared" modunu otomatik kapat.
  useEffect(() => {
      if (isTeamMode && uiGameMode === 'shared') {
          setUiGameMode('classic');
          toast.info("Shared mode cannot be used in team battles. Reverted to Classic.");
      }
  }, [isTeamMode, uiGameMode]);

  // 🛡️ MANTIK ZIRHI: Crazy/Terminal yalnızca classic & jackpot ile geçerli.
  // Base mod shared/biggest_pull olursa modifier'ları otomatik kapat (backend de reddederdi).
  useEffect(() => {
      if (!modifiersAllowed) {
          if (isCrazy) setIsCrazy(false);
          if (isTerminal) setIsTerminal(false);
      }
  }, [modifiersAllowed, isCrazy, isTerminal]);

  const totalCost = selectedCases.reduce((sum, c) => sum + c.price, 0);

  const addCase = (c) => {
    if (selectedCases.length >= 30) return toast.error("You can add max 30 cases!");
    setSelectedCases(prev => [...prev, { ...c, tempId: Math.random() }]);
  };

  // Belirli bir kasanın kaç adet eklendiğini döndürür
  const quantityOf = (caseId) => selectedCases.reduce((n, c) => n + (c.id === caseId ? 1 : 0), 0);

  // O kasadan SADECE 1 tane azalt (en son eklenen örneği çıkarır)
  const decrementCase = (caseId) => {
    setSelectedCases(prev => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) { if (prev[i].id === caseId) { lastIdx = i; break; } }
      if (lastIdx === -1) return prev;
      const next = [...prev];
      next.splice(lastIdx, 1);
      return next;
    });
  };

  // O kasanın TAMAMINI çıkar
  const removeAllOfCase = (caseId) => {
    setSelectedCases(prev => prev.filter(c => c.id !== caseId));
  };

  // Sağ taraf listesi: aynı kasaları grupla + adet bilgisi (ilk ekleme sırası korunur)
  const groupedSelected = useMemo(() => {
    const map = new Map();
    for (const c of selectedCases) {
      if (map.has(c.id)) map.get(c.id).qty += 1;
      else map.set(c.id, { ...c, qty: 1 });
    }
    return Array.from(map.values());
  }, [selectedCases]);

  // 🔍 View Drops: itemleri düşme oranına çevir (nadirden yaygına sırala)
  const dropsItems = useMemo(() => {
    if (!dropsModalCase) return [];
    const items = dropsModalCase.items || [];
    const total = items.reduce((s, it) => s + (it.weight || 0), 0) || 1;
    return [...items]
      .map(it => ({ ...it, chance: ((it.weight || 0) / total) * 100 }))
      .sort((a, b) => (a.weight || 0) - (b.weight || 0));
  }, [dropsModalCase]);

  const handleCreate = async () => {
    if (!isAuthenticated) return toast.error("Please log in first!");
    if (selectedCases.length === 0) return toast.error("Select at least one case!");
    
    setIsCreating(true);
    try {
      const caseIds = selectedCases.map(c => c.id);

      // 🌟 Backend formatı: base game_mode + iki modifier (crazy/terminal).
      // Modifier'lar yalnızca classic/jackpot ile gönderilir; aksi halde zorla kapatılır
      // (UI zaten engelliyor ama çift güvenlik — backend de reddederdi).
      const finalIsCrazy = modifiersAllowed && isCrazy;
      const finalIsTerminal = modifiersAllowed && isTerminal;

      // 🐷 BORROW: kapalıysa 0; açıksa 1..80 arası seçilen oran (güvenli clamp).
      const finalBorrow = borrowEnabled ? Math.max(1, Math.min(80, Math.round(borrowPercent))) : 0;

      const res = await axios.post(`${BACKEND_URL}/api/game/battles/create`, 
        { 
            mode, 
            game_mode: uiGameMode, 
            case_ids: caseIds, 
            is_crazy_mode: finalIsCrazy,
            is_terminal_mode: finalIsTerminal,
            fast_mode: fastMode,
            borrow_percent: finalBorrow
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Battle created!");
      navigate(`/battles/${res.data.battle_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Creation failed.");
    } finally {
      setIsCreating(false);
    }
  };

  const PlayerModeButton = ({ value, hero, sub, heroClass = 'text-sm', disabled }) => {
    const active = mode === value;
    return (
      <button
        type="button"
        onClick={() => !disabled && setMode(value)}
        disabled={disabled}
        aria-pressed={active}
        className={`py-2 px-1 rounded-lg border flex flex-col items-center justify-center gap-0.5 transition-all ${
          disabled
            ? 'bg-[#15171a] border-[#2A2D31]/50 text-zinc-600 opacity-50 cursor-not-allowed'
            : active
              ? 'bg-[#B059E8]/20 border-[#B059E8] shadow-[0_0_15px_rgba(176,89,232,0.3)] text-white'
              : 'bg-[#15171a] border-[#2A2D31] text-zinc-300 hover:border-[#B059E8]/50 hover:text-white'
        }`}
      >
        <span className={`${heroClass} font-black leading-none tracking-tight`}>{hero}</span>
        <span className={`text-[8px] font-bold uppercase tracking-widest leading-none ${active && !disabled ? 'text-[#c98bf0]' : 'text-zinc-500'}`}>{sub}</span>
      </button>
    );
  };

  const GameModeButton = ({ value, label, desc, icon, colorClass, activeColorClass, disabled }) => (
      <button 
        onClick={() => !disabled && setUiGameMode(value)}
        disabled={disabled}
        title={disabled ? undefined : desc}
        className={`w-full p-2.5 rounded-xl border flex items-center gap-2.5 transition-all text-left ${
          disabled
            ? 'bg-[#15171a] border-[#2A2D31]/30 text-zinc-700 opacity-50 cursor-not-allowed'
            : uiGameMode === value 
              ? `${activeColorClass} text-white` 
              : 'bg-[#15171a] border-[#2A2D31] text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
        }`}
      >
        <div className={`shrink-0 ${uiGameMode === value ? 'text-white drop-shadow-md' : colorClass}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest font-black leading-tight truncate">{label}</div>
            <div className="text-[9px] font-medium opacity-80 leading-tight mt-0.5 truncate">{desc}</div>
        </div>
      </button>
  );

  // 🌟 MODIFIER TOGGLE (Crazy / Terminal) — base mod classic/jackpot değilse devre dışı.
  const ModifierToggle = ({ active, onToggle, label, desc, icon, accent, disabled }) => (
      <button
        type="button"
        onClick={() => !disabled && onToggle()}
        disabled={disabled}
        aria-pressed={active}
        title={disabled ? 'Only available with Classic or Jackpot' : desc}
        className={`w-full px-2.5 py-2 rounded-xl border flex items-center gap-2.5 transition-all text-left ${
          disabled
            ? 'bg-[#15171a] border-[#2A2D31]/30 text-zinc-700 opacity-50 cursor-not-allowed'
            : active
              ? `${accent.activeCls} text-white`
              : 'bg-[#15171a] border-[#2A2D31] text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
        }`}
      >
        <div className={`shrink-0 ${active && !disabled ? 'text-white drop-shadow-md' : accent.iconCls}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest font-black leading-tight truncate">{label}</div>
            <div className="text-[9px] font-medium opacity-80 leading-tight mt-0.5 truncate">{desc}</div>
        </div>
        <div className={`shrink-0 w-8 h-[18px] rounded-full p-0.5 transition-colors ${active && !disabled ? accent.switchCls : 'bg-[#2A2D31]'}`}>
          <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-200 ${active && !disabled ? 'translate-x-[14px]' : 'translate-x-0'}`} />
        </div>
      </button>
  );

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-24 pb-12">
      <div className={`max-w-[1800px] mx-auto px-4 flex gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
        <main className="flex-1 min-w-0 flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 sm:gap-4 mb-2 border-b border-[#2A2D31] pb-2">
            <Link to="/battles" className="shrink-0 flex items-center gap-2 px-3 py-2 bg-[#1A1D20] hover:bg-[#33363b] border border-[#2A2D31] rounded-xl text-zinc-300 hover:text-white transition-colors text-[11px] font-bold uppercase tracking-widest">
              <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Lobby</span>
            </Link>
            <div className="flex items-center gap-2.5 min-w-0">
              <Swords className="w-5 h-5 sm:w-6 sm:h-6 text-[#B059E8] shrink-0 drop-shadow-[0_0_10px_rgba(176,89,232,0.5)]" />
              <h1 className="font-heading font-black text-2xl sm:text-3xl text-white uppercase tracking-wider truncate">Create Battle</h1>
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-4 xl:gap-5 items-stretch min-h-0">

            {/* ════════ BİRLEŞİK: SOL SEÇENEKLER + ORTA KASALAR (aralarında boşluk yok) ════════ */}
            {/* Yükseklik SOL RAY tarafından belirlenir (Jackpot'ta biter); orta ızgara bu yükseklik içinde scroll eder. */}
            <div className="flex-1 min-w-0 flex flex-col xl:flex-row bg-[#1A1D20] border border-[#2A2D31] rounded-2xl shadow-lg overflow-hidden">

              {/* ── SOL RAY: SEÇENEKLER (yüksekliği belirler, scroll YOK) ── */}
              <aside className="w-full xl:w-[200px] shrink-0 border-b xl:border-b-0 xl:border-r border-[#2A2D31] bg-[#1A1D20]">

                {/* PLAYERS */}
                <div className="p-3.5 border-b border-[#2A2D31]">
                  <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2.5">Players</h3>

                  {/* Free-for-all (etiketsiz) */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <PlayerModeButton value="1v1" hero="2" heroClass="text-lg" sub="Players" />
                    <PlayerModeButton value="1v1v1" hero="3" heroClass="text-lg" sub="Players" />
                    <PlayerModeButton value="1v1v1v1" hero="4" heroClass="text-lg" sub="Players" />
                    <PlayerModeButton value="1v1v1v1v1v1" hero="6" heroClass="text-lg" sub="Players" />
                  </div>

                  {/* Teams (etiketsiz) — doğrudan alt alta */}
                  <div className="grid grid-cols-3 gap-2">
                    <PlayerModeButton value="2v2" hero="2v2" sub="4P" disabled={uiGameMode === 'shared'} />
                    <PlayerModeButton value="2v2v2" hero="2v2v2" heroClass="text-xs" sub="6P" disabled={uiGameMode === 'shared'} />
                    <PlayerModeButton value="3v3" hero="3v3" sub="6P" disabled={uiGameMode === 'shared'} />
                  </div>
                </div>

                {/* GAME MODE */}
                <div className="p-3.5">
                  <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2.5">Game Mode</h3>
                  <div className="flex flex-col gap-2">
                    <GameModeButton value="classic" label="Classic" desc="Highest total wins." icon={<Swords className="w-5 h-5" />} colorClass="text-zinc-400" activeColorClass="bg-[#B059E8]/20 border-[#B059E8] shadow-[0_0_15px_rgba(176,89,232,0.3)]" />
                    <GameModeButton value="shared" label="Shared" desc="Split equally to all." icon={<Handshake className="w-5 h-5" />} colorClass="text-amber-500" activeColorClass="bg-amber-500/20 border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.3)]" disabled={isTeamMode} />
                    <GameModeButton value="biggest_pull" label="Biggest Pull" desc="Priciest item wins." icon={<Gem className="w-5 h-5" />} colorClass="text-[#B059E8]" activeColorClass="bg-[#B059E8]/20 border-[#B059E8] shadow-[0_0_15px_rgba(176,89,232,0.3)]" />
                    <GameModeButton value="jackpot" label="Jackpot" desc="Value buys pot odds." icon={<Dices className="w-5 h-5" />} colorClass="text-amber-500" activeColorClass="bg-amber-500/20 border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.3)]" />
                  </div>

                  {/* MODIFIERS — Crazy & Terminal (yalnızca Classic/Jackpot ile birleşir) */}
                  <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-3.5 mb-2.5">Modifiers</h3>
                  <div className="flex flex-col gap-2">
                    <ModifierToggle
                      active={isCrazy && modifiersAllowed}
                      onToggle={() => setIsCrazy(v => !v)}
                      disabled={!modifiersAllowed}
                      label="Crazy"
                      desc={uiGameMode === 'jackpot' ? 'Lowest = best odds.' : 'Lowest total wins.'}
                      icon={<Flame className="w-5 h-5" />}
                      accent={{ iconCls: 'text-rose-500', activeCls: 'bg-rose-500/20 border-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.3)]', switchCls: 'bg-rose-500' }}
                    />
                    <ModifierToggle
                      active={isTerminal && modifiersAllowed}
                      onToggle={() => setIsTerminal(v => !v)}
                      disabled={!modifiersAllowed}
                      label="Terminal"
                      desc={uiGameMode === 'jackpot' ? 'Last case sets odds.' : 'Last case decides.'}
                      icon={<Target className="w-5 h-5" />}
                      accent={{ iconCls: 'text-cyan-400', activeCls: 'bg-cyan-500/20 border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.3)]', switchCls: 'bg-cyan-500' }}
                    />
                  </div>
                  {!modifiersAllowed && (
                    <p className="text-[9px] text-zinc-600 leading-tight mt-2">Modifiers work only with Classic &amp; Jackpot.</p>
                  )}

                  {/* ════════ BORROW (BORÇ) — pembe kumbara; diğer butonlar gibi seçiliyse YANAR ════════ */}
                  {/* Pembe range thumb/track stilleri (yalnız bu bölüm için scoped) */}
                  <style>{`
                    .borrow-range { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; border-radius: 9999px; outline: none; cursor: pointer; }
                    .borrow-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 14px; height: 14px; border-radius: 9999px; background: #ec4899; border: 2px solid rgba(255,255,255,0.85); box-shadow: 0 0 8px rgba(236,72,153,0.75); cursor: pointer; margin-top: -1px; }
                    .borrow-range::-moz-range-thumb { width: 14px; height: 14px; border-radius: 9999px; background: #ec4899; border: 2px solid rgba(255,255,255,0.85); box-shadow: 0 0 8px rgba(236,72,153,0.75); cursor: pointer; }
                    .borrow-range::-moz-range-track { height: 4px; border-radius: 9999px; background: transparent; }
                  `}</style>
                  <button
                    type="button"
                    onClick={() => setBorrowEnabled(v => !v)}
                    aria-pressed={borrowEnabled}
                    title="Unlock larger battles — cheaper entry"
                    className={`w-full mt-2 p-2.5 rounded-xl border flex items-center gap-2.5 transition-all text-left ${
                      borrowEnabled
                        ? 'bg-pink-500/20 border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.35)] text-white'
                        : 'bg-[#15171a] border-[#2A2D31] text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'
                    }`}
                  >
                    <div className={`shrink-0 ${borrowEnabled ? 'text-pink-400 drop-shadow-[0_0_6px_rgba(236,72,153,0.7)]' : 'text-pink-500/70'}`}>
                      <PiggyBank className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-widest font-black leading-tight truncate">Borrow</div>
                      <div className="text-[9px] font-medium opacity-80 leading-tight mt-0.5 truncate">Unlock larger battles</div>
                    </div>
                  </button>

                  {/* İNCE BAR — yalnızca borrow AÇIKKEN görünür; kapatılınca yok olur. Sol min %1, sağ max %80. */}
                  {borrowEnabled && (
                    <div className="mt-2.5 px-0.5">
                      <div className="flex items-center justify-center mb-1.5">
                        <span className="text-sm mm-num font-black text-pink-400 leading-none">{borrowPercent}%</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={80}
                        step={1}
                        value={borrowPercent}
                        onChange={(e) => setBorrowPercent(Number(e.target.value))}
                        className="borrow-range"
                        style={{
                          background: `linear-gradient(to right, #ec4899 0%, #ec4899 ${((borrowPercent - 1) / 79) * 100}%, #2A2D31 ${((borrowPercent - 1) / 79) * 100}%, #2A2D31 100%)`
                        }}
                        aria-label="Borrow percent"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[9px] mm-num font-bold text-zinc-600">1%</span>
                        <span className="text-[9px] mm-num font-bold text-zinc-600">80%</span>
                      </div>
                    </div>
                  )}
                </div>

              </aside>

              {/* ── ORTA: ARAMA + KASA IZGARASI (sol ray yüksekliğini doldurur, içeride scroll) ── */}
              <div className="flex-1 min-w-0 relative flex flex-col">
                <div className="xl:absolute xl:inset-0 flex flex-col min-h-0">

                  {/* Filtre çubuğu: arama belirgin, butonlar küçük */}
                  <div className="p-3 border-b border-[#2A2D31] bg-[#15171a] shrink-0">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-2.5">

                      {/* ARAMA — ana eleman */}
                      <div className="relative flex-1 min-w-0">
                        <Search className="w-4 h-4 text-[#B059E8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          placeholder="Search cases by name..."
                          className="w-full bg-[#1A1D20] border border-[#2A2D31] rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-[#B059E8] focus:ring-1 focus:ring-[#B059E8]/30 transition-colors"
                        />
                        {search && (
                          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors" aria-label="Clear search">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Kaynak + Sıralama — küçük kontroller */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        {/* Kaynak: Official / Community */}
                        <div className="flex items-center gap-0.5 p-0.5 bg-[#1A1D20] rounded-lg border border-[#2A2D31]">
                          {[
                            { k: 'donutluck', label: 'Official' },
                            { k: 'community', label: 'Community' },
                          ].map(s => (
                            <button
                              key={s.k}
                              onClick={() => setCaseSource(s.k)}
                              className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-wider transition-all ${
                                caseSource === s.k
                                  ? 'bg-[#2A2D31] text-white border border-[#B059E8]/40'
                                  : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>

                        {/* Sıralama: Popular / Price */}
                        <div className="flex items-center gap-0.5 p-0.5 bg-[#1A1D20] rounded-lg border border-[#2A2D31]">
                          {[
                            { k: 'popularity', label: 'Popular' },
                            { k: 'price', label: 'Price' },
                          ].map(s => (
                            <button
                              key={s.k}
                              onClick={() => setSortBy(s.k)}
                              className={`px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all ${
                                sortBy === s.k
                                  ? 'bg-[#2A2D31] text-white border border-[#B059E8]/40'
                                  : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>

                        {/* Yön */}
                        <button
                          onClick={() => setSortDir(d => (d === 'high' ? 'low' : 'high'))}
                          title={sortDir === 'high' ? 'High to low' : 'Low to high'}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1A1D20] border border-[#2A2D31] text-zinc-300 hover:text-white hover:border-[#B059E8]/40 text-[9px] font-black uppercase tracking-wider transition-all shrink-0"
                        >
                          {sortDir === 'high'
                            ? <><ArrowDown className="w-3 h-3" /> High</>
                            : <><ArrowUp className="w-3 h-3" /> Low</>}
                        </button>
                      </div>

                    </div>
                  </div>

                  {/* Kasa ızgarası — burada scroll eder */}
                  <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-4 gap-3 content-start scrollbar-thin max-h-[70vh] xl:max-h-none">
                    {poolLoading ? (
                      <div className="col-span-full py-12 flex justify-center"><Activity className="w-6 h-6 text-[#B059E8] animate-spin" /></div>
                    ) : displayedCases.length === 0 ? (
                      <div className="col-span-full py-16 flex flex-col items-center justify-center text-zinc-600">
                        <Swords className="w-10 h-10 mb-3 opacity-20" />
                        <p className="text-xs font-bold uppercase tracking-widest text-center px-4">
                          {debouncedSearch
                            ? `No cases match "${debouncedSearch}"`
                            : caseSource === 'community' ? 'No community cases yet' : 'No cases available'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {displayedCases.map(c => {
                          const qty = quantityOf(c.id);
                          return (
                          <div 
                            key={c.id} 
                            className={`relative bg-gradient-to-b from-[#1f2226] to-[#15171a] border rounded-xl p-3 flex flex-col items-center transition-all shadow-md hover:-translate-y-0.5 ${qty > 0 ? 'border-[#B059E8]/60 shadow-[0_0_0_1px_rgba(176,89,232,0.15)]' : 'border-[#2A2D31] hover:border-[#B059E8]/40'}`}
                          >
                            {/* Seçili adet rozeti */}
                            {qty > 0 && (
                              <span className="absolute top-2 right-2 z-20 px-1.5 py-0.5 rounded-md bg-[#B059E8] text-white text-[9px] font-black mm-num leading-none shadow-md">×{qty}</span>
                            )}

                            {/* KASA GÖRSELİ + HOVER "VIEW DROPS" */}
                            <button
                              type="button"
                              onClick={() => setDropsModalCase(c)}
                              title="View drops"
                              className="relative w-24 h-24 flex items-center justify-center group/img focus:outline-none"
                            >
                              <div className="w-24 h-24 flex items-center justify-center transition-all duration-200 group-hover/img:opacity-10 group-hover/img:blur-[2px]">
                                {c.image_url?.startsWith('color:') ? (
                                    <div className="w-24 h-24 relative flex items-center justify-center drop-shadow-lg">
                                        {/* 📏 0.40 → 0.62: kutu ile ölçek ÇARPIYOR; kasa
                                            CaseOpening'deki boyuta yaklaşıyor. */}
                                        <div className="absolute transform scale-[0.62] origin-center flex items-center justify-center w-full h-full">
                                            <DynamicCasePreview caseColor={c.image_url} items={c.items || []} showcase={c.showcase} />
                                        </div>
                                    </div>
                                ) : (
                                    <img src={c.image_url} alt={c.name} className="w-24 h-24 object-contain drop-shadow-lg" />
                                )}
                              </div>
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity">
                                <Search className="w-5 h-5 text-[#B059E8] drop-shadow-md" />
                                <span className="text-[9px] font-black uppercase tracking-widest text-white">View Drops</span>
                              </div>
                            </button>
                            
                            <p className="text-xs font-bold text-white uppercase mt-2 truncate w-full text-center">{c.name}</p>
                            <p className="text-xs mm-num text-[#2ecf6e] font-bold mt-1 mb-2.5"><WalletValue amount={c.price} wallet={wallet} /></p>

                            {/* ADD CASE → tıklanınca "− adet +" sayacına dönüşür */}
                            {qty === 0 ? (
                              <button
                                onClick={() => addCase(c)}
                                className="w-full py-1.5 rounded-lg bg-[#B059E8] hover:bg-[#c98bf0] border border-[#B059E8] text-[10px] font-black uppercase tracking-widest text-white transition-all flex items-center justify-center gap-1 shadow-[0_0_12px_rgba(176,89,232,0.35)]"
                              >
                                <Plus className="w-3 h-3" /> Add Case
                              </button>
                            ) : (
                              <div className="w-full flex items-center justify-between gap-1 bg-[#15171a] border border-[#B059E8]/40 rounded-lg p-1">
                                <button onClick={() => decrementCase(c.id)} className="w-7 h-7 rounded-md bg-[#2A2D31] hover:bg-rose-500/30 text-white flex items-center justify-center transition-colors" aria-label="Remove one">
                                  <Minus className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-sm font-black text-white mm-num select-none">{qty}</span>
                                <button onClick={() => addCase(c)} className="w-7 h-7 rounded-md bg-[#2A2D31] hover:bg-[#B059E8]/40 text-white flex items-center justify-center transition-colors" aria-label="Add one">
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                          );
                        })}

                        {/* Community: Load More */}
                        {caseSource === 'community' && communityHasMore && (
                          <button
                            onClick={loadMoreCommunity}
                            disabled={communityLoadingMore}
                            className="col-span-full mt-1 py-3 rounded-xl bg-[#1A1D20] border border-[#2A2D31] hover:border-[#B059E8]/50 text-zinc-300 hover:text-white text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {communityLoadingMore ? <Activity className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Load More
                          </button>
                        )}
                      </>
                    )}
                  </div>

                </div>
              </div>

            </div>

            {/* ════════ SAĞ: SEÇİLİ / BATTLE KASALARI (ince, aynı yüksekliği doldurur) ════════ */}
            <div className="xl:w-[260px] shrink-0 relative bg-[#1A1D20] border border-[#2A2D31] rounded-2xl shadow-lg overflow-hidden flex flex-col">
              <div className="xl:absolute xl:inset-0 flex flex-col min-h-0">

                {/* Başlık + Clear All */}
                <div className="p-4 border-b border-[#2A2D31] bg-[#15171a] flex items-center justify-between shrink-0">
                  <h3 className="text-[10px] font-black text-white uppercase tracking-widest">Selected · {selectedCases.length}/30</h3>
                  {selectedCases.length > 0 && (
                    <button onClick={() => setSelectedCases([])} className="text-[10px] font-bold text-rose-400 hover:text-rose-300 uppercase tracking-widest transition-colors">Clear All</button>
                  )}
                </div>

                {/* Eklenmiş kasalar — gruplanmış + adetli */}
                <div className="flex-1 min-h-0 overflow-y-auto p-3 scrollbar-thin max-h-[50vh] xl:max-h-none">
                  {groupedSelected.length === 0 ? (
                    <div className="h-full min-h-[180px] flex flex-col items-center justify-center text-zinc-600 text-center px-4 border-2 border-dashed border-[#2A2D31] rounded-xl">
                      <Plus className="w-8 h-8 mb-2 opacity-40" />
                      <p className="text-xs font-bold uppercase tracking-widest">Click cases to add them here</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2.5">
                      <AnimatePresence>
                        {groupedSelected.map(c => (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                            key={c.id}
                            onClick={() => decrementCase(c.id)}
                            title="Click to remove one"
                            className="aspect-[5/6] bg-gradient-to-br from-[#1f2226] to-[#15171a] border border-[#2A2D31] hover:border-[#B059E8]/50 rounded-xl flex flex-col items-center justify-center relative group cursor-pointer shadow-md"
                          >
                            {/* Sağ üst ÇARPI → bu kasanın HEPSİNİ çıkar */}
                            <button
                              onClick={(e) => { e.stopPropagation(); removeAllOfCase(c.id); }}
                              title="Remove all"
                              className="absolute -top-1.5 -right-1.5 z-30 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-400 text-white flex items-center justify-center shadow-md transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>

                            {c.qty > 1 && (
                              <span className="absolute top-1 left-1 z-20 px-1.5 py-0.5 rounded-md bg-[#B059E8] text-white text-[9px] font-black mm-num leading-none">×{c.qty}</span>
                            )}

                            {/* Gövdeye tıkla → 1 azalt (hover'da yazı yok, hafif vurgu) */}
                            <div className="absolute inset-0 bg-rose-500/5 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity z-10 pointer-events-none" />

                            {/* Görsel */}
                            {c.image_url?.startsWith('color:') ? (
                                <div className="w-20 h-20 relative flex items-center justify-center z-0 mb-1.5">
                                    {/* 📏 Izgara kartı: kutu ve ölçek birlikte büyütüldü. */}
                                    <div className="absolute transform scale-[0.55] origin-center flex items-center justify-center w-full h-full">
                                        <DynamicCasePreview caseColor={c.image_url} items={c.items || []} showcase={c.showcase} />
                                    </div>
                                </div>
                            ) : (
                                <img src={c.image_url} alt={c.name} className="w-20 h-20 object-contain drop-shadow-md z-0 mb-1.5" />
                            )}

                            <span className="text-[11px] mm-num text-[#2ecf6e] font-bold z-0"><WalletValue amount={c.price} wallet={wallet} /></span>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Fiyat ve Buton */}
                <div className="p-4 bg-[#15171a] border-t border-[#2A2D31] shrink-0">
                  {/* ⚡ Fast Mode — kompakt toggle (oluşturma seçeneği olduğu için aksiyon alanında) */}
                  <button
                    type="button"
                    onClick={() => setFastMode(f => !f)}
                    aria-pressed={fastMode}
                    title="Cases spin & open faster"
                    className={`w-full mb-3 px-3 py-2 rounded-xl border flex items-center gap-2.5 transition-all text-left ${
                      fastMode
                        ? 'bg-amber-500/15 border-amber-500/60 text-white'
                        : 'bg-[#1A1D20] border-[#2A2D31] text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    <Zap className={`w-4 h-4 shrink-0 ${fastMode ? 'text-amber-400 drop-shadow-[0_0_6px_rgba(245,158,11,0.6)]' : 'text-amber-500/70'}`} />
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <span className="text-[10px] font-black uppercase tracking-widest leading-none">Fast Mode</span>
                      <span className="text-[9px] text-zinc-500 leading-none mt-0.5">Faster spins &amp; openings</span>
                    </div>
                    <div className={`shrink-0 w-8 h-[18px] rounded-full p-0.5 transition-colors ${fastMode ? 'bg-amber-500' : 'bg-[#2A2D31]'}`}>
                      <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-200 ${fastMode ? 'translate-x-[14px]' : 'translate-x-0'}`} />
                    </div>
                  </button>

                  <div className="flex items-end justify-between mb-3">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Total Cost</span>
                    <div className="flex flex-col items-end">
                      <div className={`font-black mm-num flex items-center gap-1 drop-shadow-md ${borrowEnabled ? 'text-base text-zinc-500 line-through decoration-pink-500/60 decoration-2' : 'text-2xl text-white'}`}>
                        {/* Backend kasa fiyatlarını aktif cüzdanın biriminde gönderiyor. */}
                        <WalletValue amount={totalCost} wallet={wallet} />
                      </div>
                      {borrowEnabled && (
                        <div className="text-2xl font-black text-white mm-num flex items-center gap-1.5 drop-shadow-md leading-none">
                          <PiggyBank className="w-4 h-4 text-pink-400 drop-shadow-[0_0_6px_rgba(236,72,153,0.6)]" />
                          <WalletValue amount={Math.round(totalCost * (100 - borrowPercent) / 100)} wallet={wallet} />
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={handleCreate}
                    disabled={isCreating || selectedCases.length === 0}
                    className={`w-full py-3.5 rounded-xl font-black uppercase tracking-widest text-sm transition-all shadow-lg flex justify-center items-center gap-2 ${
                      selectedCases.length === 0 || isCreating
                        ? 'bg-[#2A2D31]/50 text-zinc-500 cursor-not-allowed border border-[#2A2D31]' 
                        : 'bg-gradient-to-r from-[#16A34A] to-[#15913f] hover:from-[#2ecf6e] hover:to-[#16A34A] text-white shadow-[#16A34A]/25 hover:scale-[1.02]'
                    }`}
                  >
                    {isCreating ? <Activity className="w-5 h-5 animate-spin" /> : <><Swords className="w-5 h-5" /> CREATE BATTLE</>}
                  </button>
                </div>

              </div>
            </div>

          </div>
        </main>
      </div>

      {/* 🔍 VIEW DROPS — Kasa içeriği & oranları (overview) */}
      <AnimatePresence>
        {dropsModalCase && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDropsModalCase(null)}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#15171a] border border-[#2A2D31] rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl overflow-hidden"
            >
              {/* Modal başlık */}
              <div className="p-4 border-b border-[#2A2D31] flex items-center justify-between gap-3 bg-[#1A1D20]">
                <div className="flex items-center gap-2 min-w-0">
                  <Search className="w-4 h-4 text-[#B059E8] shrink-0" />
                  <h3 className="text-sm font-black text-white uppercase tracking-widest truncate">{dropsModalCase.name}</h3>
                  <span className="text-[10px] mm-num text-[#2ecf6e] font-bold shrink-0"><WalletValue amount={dropsModalCase.price} wallet={wallet} /> $</span>
                </div>
                <button onClick={() => setDropsModalCase(null)} className="text-zinc-500 hover:text-white transition-colors shrink-0" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Item listesi + oranlar */}
              <div className="overflow-y-auto p-4 space-y-2 scrollbar-thin">
                {dropsItems.length === 0 ? (
                  <p className="text-center text-zinc-600 text-xs py-10 uppercase tracking-widest font-bold">No drop data available</p>
                ) : (
                  dropsItems.map((it, i) => {
                    const itemPrice = it.price ?? it.value;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-[#1A1D20] border border-[#2A2D31] rounded-xl p-2.5">
                        <div className="w-10 h-10 shrink-0 flex items-center justify-center">
                          {it.image_url
                            ? <img src={it.image_url} alt={it.name || 'Item'} className="w-10 h-10 object-contain drop-shadow" />
                            : <Gem className="w-6 h-6 text-zinc-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{it.name || 'Item'}</p>
                          {itemPrice != null && <p className="text-[10px] mm-num text-[#2ecf6e]"><WalletValue amount={itemPrice} wallet={wallet} /></p>}
                        </div>
                        <span className="text-xs mm-num font-black text-[#B059E8] shrink-0">{it.chance.toFixed(it.chance < 1 ? 3 : 2)}%</span>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};